import { useCallback, useEffect, useRef } from 'react';
import { useSupabaseRealtime } from './useSupabaseRealtime';

const DEBOUNCE_MS = 500;

const DASHBOARD_TABLES = [
  'tasks',
  'businesses',
  'pull_requests',
  'llm_accounts',
  'daily_priorities',
] as const;

/**
 * Orchestrates Supabase Realtime subscriptions for all 5 dashboard tables.
 * Calls `onInvalidate` (debounced to at most once per 500 ms) whenever an
 * INSERT / UPDATE / DELETE arrives from any of those tables.
 *
 * React Query (or any other data-fetching layer) stays the single source of
 * truth. Realtime is enhancement-only: it triggers a re-fetch but never holds
 * data itself.
 *
 * Graceful degradation: if Realtime is unavailable or the WebSocket handshake
 * fails, the dashboard simply won't auto-update — it will still show the data
 * from the initial fetch.
 */
export function useRealtimeDashboard(
  onInvalidate: () => void,
  tenantId?: string,
): void {
  // Keep a stable ref so the debounced callback always calls the latest version
  const onInvalidateRef = useRef(onInvalidate);
  useEffect(() => {
    onInvalidateRef.current = onInvalidate;
  }, [onInvalidate]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable debounced function — never recreated, reads from ref on each call
  const debouncedInvalidate = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onInvalidateRef.current();
    }, DEBOUNCE_MS);
  }, []); // intentionally empty — only created once

  // Clean up any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const filter = tenantId !== undefined ? `tenant_id=eq.${tenantId}` : undefined;

  // One subscription per table — Rules of Hooks: unconditional, fixed count
  useSupabaseRealtime({ table: DASHBOARD_TABLES[0], filter }, debouncedInvalidate);
  useSupabaseRealtime({ table: DASHBOARD_TABLES[1], filter }, debouncedInvalidate);
  useSupabaseRealtime({ table: DASHBOARD_TABLES[2], filter }, debouncedInvalidate);
  useSupabaseRealtime({ table: DASHBOARD_TABLES[3], filter }, debouncedInvalidate);
  useSupabaseRealtime({ table: DASHBOARD_TABLES[4], filter }, debouncedInvalidate);
}
